import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import {
  useScroll,
  useSpring,
  useTransform,
  type MotionValue
} from 'framer-motion';

type ScrollOptions = NonNullable<Parameters<typeof useScroll>[0]>;

type UseParallaxScrollOptions = {
  disableOnMobile?: boolean;
  mobileBreakpoint?: string;
  offset?: ScrollOptions['offset'];
  outputRange?: [number, number];
  spring?: {
    stiffness?: number;
    damping?: number;
    mass?: number;
  };
};

type UseParallaxScrollReturn = {
  sectionRef: MutableRefObject<HTMLElement | null>;
  parallaxValue: MotionValue<number>;
  isParallaxEnabled: boolean;
};

const DEFAULT_SPRING = {
  stiffness: 120,
  damping: 24,
  mass: 0.6
};

export const useParallaxScroll = (
  options: UseParallaxScrollOptions = {}
): UseParallaxScrollReturn => {
  const {
    disableOnMobile = true,
    mobileBreakpoint = '(max-width: 1024px)',
    offset = ['start end', 'end start'],
    outputRange = [0, -140],
    spring = DEFAULT_SPRING
  } = options;

  const sectionRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset });

  const parallaxRange = useTransform(scrollYProgress, [0, 1], outputRange);
  const parallaxSpring = useSpring(parallaxRange, spring);

  const [isParallaxEnabled, setIsParallaxEnabled] = useState(!disableOnMobile);

  useEffect(() => {
    if (!disableOnMobile) {
      setIsParallaxEnabled(true);
      return;
    }

    const mediaQuery = window.matchMedia(mobileBreakpoint);

    const handleChange = () => {
      setIsParallaxEnabled(!mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [disableOnMobile, mobileBreakpoint]);

  useEffect(() => {
    if (!isParallaxEnabled) {
      parallaxSpring.set(0);
    }
  }, [isParallaxEnabled, parallaxSpring]);

  return {
    sectionRef,
    parallaxValue: parallaxSpring,
    isParallaxEnabled
  };
};

export default useParallaxScroll;
